import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { Injector, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { authFeature } from '../../features/auth/state';
import { environment } from '../../../environments/environment';
import { TokenRefreshService } from '../services/token-refresh.service';

// Never receive Authorization and never trigger a renewal. /auth/change-password is not here:
// it is a normal authenticated request.
const PUBLIC_AUTH_PATHS = [
  '/auth',
  '/auth/refresh',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/reset-password/validate',
];

// How far a request has come in its recovery. A request is replayed at most twice: once after a
// stale-token or adopted pair, and once after a renewal.
//  - original: first attempt.
//  - stale: replay after a stale token or an adopted pair; one renewal is still allowed.
//  - renewed: replay after a real refresh call; a 401 here ends the session.
//  - last: replay after a second adopted pair; a 401 here just propagates, no refresh happened.
type Stage = 'original' | 'stale' | 'renewed' | 'last';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const store = inject(Store);
  // Resolved lazily: the renewal service uses HttpClient, which is built from this chain.
  const injector = inject(Injector);
  const token = store.selectSignal(authFeature.selectToken);
  const requestUrl = new URL(req.url, environment.apiUrl);

  // Public auth endpoints and requests that bring their own credential are left alone.
  if (PUBLIC_AUTH_PATHS.includes(requestUrl.pathname) || req.headers.has('Authorization')) {
    return next(req);
  }

  const sentToken = token();

  if (!sentToken) {
    return next(req);
  }

  if (requestUrl.origin !== new URL(environment.apiUrl).origin) {
    return next(withBearer(req, sentToken));
  }

  const send = (bearer: string, stage: Stage): Observable<HttpEvent<unknown>> =>
    next(withBearer(req, bearer)).pipe(
      catchError((error: unknown) =>
        isUnauthorized(error)
          ? recover(error, bearer, stage, send, token(), injector)
          : throwError(() => error),
      ),
    );

  return send(sentToken, 'original');
};

function recover(
  error: HttpErrorResponse,
  failedToken: string,
  stage: Stage,
  send: (bearer: string, stage: Stage) => Observable<HttpEvent<unknown>>,
  currentToken: string | null,
  injector: Injector,
): Observable<HttpEvent<unknown>> {
  const refresh = injector.get(TokenRefreshService);

  // A rejection right after a real refresh means the session is not valid.
  if (stage === 'renewed') {
    refresh.endSession();

    return throwError(() => error);
  }

  // No refresh call backed this replay, so the session is not proven invalid: the next request
  // tries again.
  if (stage === 'last') {
    return throwError(() => error);
  }

  if (!currentToken) {
    return throwError(() => error);
  }

  // The request went out before another one renewed the token: no renewal needed yet.
  if (stage === 'original' && currentToken !== failedToken) {
    return send(currentToken, 'stale');
  }

  return refresh.renew(failedToken).pipe(
    switchMap((outcome) => {
      // Handing back the token that just failed would only repeat the rejection.
      if (outcome.status !== 'renewed' || outcome.token === failedToken) {
        return throwError(() => error);
      }

      // A pair adopted from storage may itself be expired, so it earns one more renewal; a pair
      // from a real refresh call is final.
      let nextStage: Stage = 'renewed';

      if (outcome.adopted) {
        nextStage = stage === 'original' ? 'stale' : 'last';
      }

      return send(outcome.token, nextStage);
    }),
  );
}

function isUnauthorized(error: unknown): error is HttpErrorResponse {
  return error instanceof HttpErrorResponse && error.status === 401;
}

function withBearer(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}
